import Eyebrow from '@Guidelines/Component/Eyebrow';
import { theme } from '@Guidelines/Theme/theme';
import ReevaluationSectionCheckbox from '@UI/Pages/DocumentTemplates/Shared/Declaration/ReevaluationSectionCheckbox';
import DeclarationFieldRow from '@UI/Pages/DocumentTemplates/Shared/Declaration/DeclarationFieldRow';
import DeclarationGroupView from '@UI/Pages/DocumentTemplates/Shared/Declaration/DeclarationGroupView';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Tag } from '@verclam/ui-kit';

const ROOT_GROUP_NAME = 'Variables du document';

const groupStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

/** A nested group is indented and tied to its parent by a vertical rule, so depth reads at a glance. */
const nestedGroupStyle = css`
	padding-left: 0.75rem;
	border-left: 2px solid ${theme.colors.border};
`;

const headStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

const subtitleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.inkMuted};
`;

const entriesStyle = css`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
`;

const entryStyle = css`
	& + & {
		border-top: 2px solid ${theme.colors.border};
	}
`;

const emptyStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly group: DeclarationGroupView;
	readonly depth?: number;
	readonly reevaluationSectionKeys?: readonly string[];
	readonly onToggleReevaluationSection?: (sectionKey: string) => void;
};

/**
 * Recursive rendering of a declared group: first-level groups carry an `Eyebrow` heading, nested
 * ones a quieter title, indented under a rule. The reevaluation checkbox only shows on a first-level
 * group, and only when the caller supplies a toggle handler.
 */
const DeclarationGroup: React.FC<Props> = ({
	group,
	depth = 0,
	reevaluationSectionKeys = [],
	onToggleReevaluationSection,
}) => {
	const isNested = depth > 0;
	const name = group.name ?? ROOT_GROUP_NAME;

	return (
		<div css={[groupStyle, isNested ? nestedGroupStyle : null]}>
			<div css={headStyle}>
				{isNested ? <p css={subtitleStyle}>{name}</p> : <Eyebrow>{name}</Eyebrow>}
				{group.isList ? <Tag value="liste" severity="secondary" /> : null}
			</div>

			{group.isReevaluationMarkable && onToggleReevaluationSection !== undefined ? (
				<ReevaluationSectionCheckbox
					sectionKey={group.key}
					checked={reevaluationSectionKeys.includes(group.key)}
					onToggle={onToggleReevaluationSection}
				/>
			) : null}

			{group.entries.length === 0 ? (
				<p css={emptyStyle}>Aucune variable dans ce groupe.</p>
			) : (
				<ul css={entriesStyle}>
					{group.entries.map((entry) => (
						<li
							key={entry instanceof DeclarationGroupView ? `group-${entry.key}` : entry.uuid}
							css={entryStyle}
						>
							{entry instanceof DeclarationGroupView ? (
								<DeclarationGroup group={entry} depth={depth + 1} />
							) : (
								<DeclarationFieldRow field={entry} />
							)}
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

export default DeclarationGroup;
